import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, MessageCircle, Heart, Share2, Bell, BellOff, MoreHorizontal, Ban, Copy, X, Search, Plus, Settings, Image as ImageIcon, Users, Info, MapPin } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { IdentityTag } from './IdentityTag';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';
import { useYapInteractions } from './YapInteractionsContext';
import { YapImageGrid } from './ui/YapImageGrid';
import { YapVideoPlayer } from './ui/YapVideoPlayer';
import { formatMemberCount } from './utils/memberCount';
import { PressableCard } from './ui/PressableCard';
import { LikeButton } from './ui/LikeButton';
import { YapTopicLabel } from './ui/YapTopicLabel';
import { FriendBadge } from './ui/FriendBadge';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { ShareYapModal } from './ShareYapModal';
import { ShareGroupModal } from './ShareGroupModal';
import { ParallaxBackground } from './utils/parallax';
import { ForumService } from '../services/api';
import { Yap, UserRank, Forum, User } from '../types';
import { GroupSettingsModal } from './GroupSettingsModal';
import { getRankRingParams, getRankBadge } from './utils/rankUtils';
import RankBadge from './RankBadge';
import { useTheme } from './ThemeProvider';
import { UserService } from '../services/api';
import { EVENTS, SCREENS } from '../constants/navigation';
import { FormattedText } from './FormattedText';
import { PollCard } from './ui/PollCard';
import { GroupMembersModal } from './GroupMembersModal';

import { FlagImage } from './utils/countryFlags';
import { PostMenu } from './ui/PostMenu';

interface HistoryForumScreenProps {
  topic: any;
  onBack: () => void;
}

import { getGroupGradient } from './utils/groupUtils';
import { useScreenReady } from '../contexts/ScreenTransitionContext';

export function HistoryForumScreen({ topic: initialTopic, onBack }: HistoryForumScreenProps) {
  const { userProfile, firebaseUser, joinForum, leaveForum, isForumMember, isFriend } = useUserData();
  const { isBlockedContent } = useContentFilter();
  const { theme } = useTheme();
  const [topic, setTopic] = useState(initialTopic);
  const [isJoined, setIsJoined] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedYap, setSelectedYap] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState<User | null>(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [newPostImages, setNewPostImages] = useState<string[]>([]);
  const [discussions, setDiscussions] = useState<Yap[]>([]);
  const [loading, setLoading] = useState(true);
  useScreenReady(!loading);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);


  // Load posts
  useEffect(() => {
    const loadPosts = async () => {
      if (topic && topic.id) {
        setLoading(true);

        // Ensure forum document exists in Firestore (for count tracking)
        try {
          const { getDbSafe } = await import('../services/firebaseConfig');
          const { doc, getDoc, setDoc, serverTimestamp, collection, getCountFromServer } = await import('firebase/firestore');
          const db = await getDbSafe();
          const forumRef = doc(db, "forums", topic.id);
          const forumSnap = await getDoc(forumRef);

          let effectiveOwnerId = topic.ownerId;

          // Live count from the members subcollection. Cheap aggregate query
          // — doesn't pull doc bodies. We pair this with the legacy `members`
          // array on the forum doc and the denormalized memberCount counter,
          // taking the MAX so historic joiners (who exist only in the legacy
          // array) aren't dropped from the count.
          let liveMemberCount: number | null = null;
          try {
            const membersAgg = await getCountFromServer(collection(db, 'forums', topic.id, 'members'));
            liveMemberCount = membersAgg.data().count;
          } catch (countErr) {
            console.warn('[HistoryForum] Failed to count members subcollection:', countErr);
          }

          if (forumSnap.exists()) {
            const forumData = forumSnap.data();
            // 🛡️ SYNC: Pull core data (including banners) from Firestore document.
            // Firestore is the source of truth for ownerId — prefer it over the
            // (possibly stale) prop so a passed-down ownership change is reflected
            // and the previous owner is correctly demoted to a regular member.
            // Fall back to the prop only when the doc has no ownerId yet.
            if (forumData.ownerId) {
              effectiveOwnerId = forumData.ownerId;
            }
            const legacyLen = Array.isArray(forumData.members) ? forumData.members.length : 0;
            const denorm = typeof forumData.memberCount === 'number' ? forumData.memberCount : 0;
            const resolvedMemberCount = Math.max(legacyLen, denorm, liveMemberCount ?? 0);
            // Update state so the UI instantly reflects any new banners/details
            setTopic((prev: any) => ({
               ...prev,
               ownerId: effectiveOwnerId,
               bannerUrl: forumData.bannerUrl || forumData.bannerImage || forumData.image || prev.bannerUrl || '',
               image: forumData.image || prev.image || '',
               bannerImage: forumData.bannerImage || prev.bannerImage || '',
               title: forumData.title || forumData.name || prev.title,
               name: forumData.name || forumData.title || prev.name,
               description: forumData.description !== undefined ? forumData.description : prev.description,
               memberCount: resolvedMemberCount || prev.memberCount,
               postCount: forumData.postCount ?? prev.postCount,
               // Needed by the share sheet: without it every share attempt looks
               // like a first mint and the rules reject the write.
               inviteCode: forumData.inviteCode ?? prev.inviteCode,
            }));
          } else {
            console.log(`[HistoryForum] Initializing forum document for: ${topic.id}`);
            const forumName = topic.title || topic.name || 'Forum';
            await setDoc(forumRef, {
              name: forumName,
              title: forumName,
              description: topic.description || '',
              bannerUrl: topic.bannerUrl || topic.image || '',
              postCount: 0,
              ownerId: topic.ownerId || null, // 🛡️ Preserve ownerId if it exists in the prop
              createdAt: serverTimestamp(),
              // Discover's Group Updates orders on lastActivityAt, and Firestore
              // omits documents that lack the ordered field entirely — so a forum
              // initialized here without it stayed invisible to that section no
              // matter how much it was used. GovernanceService.createForum sets
              // the same field at creation.
              lastActivityAt: serverTimestamp(),
              type: 'history'
            }, { merge: true });
            // Even when we just initialized the doc, reflect the live count so
            // the header doesn't fall through to the "1 members" default.
            if (liveMemberCount !== null) {
              setTopic((prev: any) => ({ ...prev, memberCount: liveMemberCount }));
            }
          }

          // Fetch owner info
          if (effectiveOwnerId) {
            try {
              const owner = await UserService.fetchUserById(effectiveOwnerId);
              if (owner) {
                setOwnerInfo(owner);
              } else {
                // Fallback if user document doesn't exist but has a generic system name?
                // For now, if fetch fails, we keep ownerInfo null
              }
            } catch (e) {
              console.error("Failed to fetch forum owner:", e);
            }
          } else {
             // 🏛️ SYSTEM FALLBACK: If no ownerId, it's likely a system-created history forum
             setOwnerInfo({ name: "AfroYap Team", username: "afroyapp" } as User);
          }
        } catch (e) {
          console.error("Failed to initialize forum document:", e);
        }

        const posts = await ForumService.getPosts(topic.id);
        setDiscussions(posts);

        // Fetch all users for profile navigation lookup
        try {
          const users = await UserService.fetchAllUsers();
          setAllUsers(users);
        } catch (e) {
          console.warn("Failed to fetch all users for lookup", e);
        }

        setLoading(false);
      }
    };
    loadPosts();
  }, [topic?.id]);

  useEffect(() => {
    if (!topic.id) return;
    const joined = isForumMember(topic.id, topic.ownerId);
    console.log('[HistoryForumScreen] Member check:', {
      topicId: topic.id,
      topicOwnerId: topic.ownerId,
      userProfileId: userProfile?.id,
      firebaseUserId: firebaseUser?.uid,
      joined
    });
    setIsJoined(joined);
  }, [topic.id, topic.ownerId, isForumMember, userProfile?.id, userProfile?.originalId, firebaseUser?.uid]);

  // When ownership is passed (via the global PassOwnership modal), reflect the
  // new owner locally so the FORMER owner is immediately treated as a regular
  // member — owner-only controls disappear without needing a reload.
  useEffect(() => {
    const handleTransferred = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || detail.entityType !== 'forum' || String(detail.entityId) !== String(topic.id)) return;
      const newOwnerId = String(detail.newOwnerId || '');
      setTopic((prev: any) => ({ ...prev, ownerId: newOwnerId }));
    };
    window.addEventListener('ownershipTransferred', handleTransferred);
    return () => window.removeEventListener('ownershipTransferred', handleTransferred);
  }, [topic.id]);

  const currentUserId = userProfile?.id || firebaseUser?.uid;
  const isOwner = topic.ownerId && currentUserId && (
    String(topic.ownerId).trim() === String(currentUserId).trim() || 
    (userProfile?.originalId && String(topic.ownerId).trim() === String(userProfile.originalId).trim())
  );
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareGroup, setShowShareGroup] = useState(false);
  const [selectedYapToShare, setSelectedYapToShare] = useState<any>(null);
  const { 
    handleInteraction, 
    isPostLiked, 
    getPostLikeCount 
  } = useYapInteractions();

  const handleBlockUser = (userName: string) => {
    toast.error(`Blocked ${userName}`, {
      description: 'You will no longer see yaps from this user.',
    });
  };

  const handleCopyLink = (postId: string) => {
    toast.success('Link copied to clipboard!');
  };

  const handleViewProfile = (playerName: string) => {
    if (!playerName) return;
    
    // Proper navigation logic handling self-profile
    if (playerName === userProfile.name || playerName === userProfile.username) {
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.PROFILE }));
      return;
    }

    // Try to find the full user data from allUsers
    const fullUser = allUsers.find(u => u.name === playerName || u.username === playerName);
    if (fullUser) {
      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, { detail: fullUser }));
    } else {
      toast.info(`Opening ${playerName}'s profile...`);
      // If user not in memory, we could potentially fetch them here, but for now we follow App.tsx pattern
      // which expects a User object. App.tsx's handleFriendProfile detail is (e as CustomEvent).detail
    }
  };

  const handleReportPost = async (post: any) => {
    const reason = window.prompt('Why are you reporting this post? (Harassment, Spam, etc.)');
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error('A valid reason is required to report.');
      return;
    }
    try {
      const { reportToWatchers } = await import('../services/WatcherService');
      await reportToWatchers(
        String(currentUserId || ''),
        String(post.id),
        'forum_post',
        reason.trim(),
        {
          targetUserId: String(post.userId || post.authorId || ''),
          authorUsername: post.author,
          content: post.content,
          forumId: topic.id,
        }
      );
      toast.success('Post reported', { description: 'Our moderators will review this content.' });
    } catch (err) {
      toast.error('Failed to submit report.');
    }
  };

  const handleYapClick = (yap: any) => {
    // Navigate to YapDetailScreen instead of showing modal
    window.dispatchEvent(new CustomEvent('navigateToYapDetail', { detail: yap }));
  };

  const handleAddImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (newPostImages.length >= 2) {
      toast.error('Maximum 2 images allowed');
      return;
    }

    try {
      const url = await ForumService.uploadPostImage('forum', topic.id, firebaseUser?.uid || '', file);
      setNewPostImages(prev => [...prev, url]);
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleTriggerUpload = () => {
    imageInputRef.current?.click();
  };

  const handleRemoveImage = (index: number) => {
    setNewPostImages(newPostImages.filter((_, i) => i !== index));
  };

  const handleCreatePost = async () => {
    if (newPostContent.trim()) {
      if (newPostContent.length > 300) {
        toast.error('Yap is too long. Maximum 300 characters allowed.');
        return;
      }

      try {
        const newPost = await ForumService.createPost(topic.id, newPostContent, newPostImages);

        // Add new yap to the top of discussions
        setDiscussions([newPost, ...discussions]);

        toast.success('Yap created!', {
          description: `Your yap will only be visible in this forum`,
        });
        setNewPostContent('');
        setNewPostImages([]);
        setShowNewPostModal(false);
      } catch (error: any) {
        toast.error("Failed to create post. Please try again.");
        console.error("Error creating post:", error);
        if (error.code) console.error("Error code:", error.code);
        if (error.message) console.error("Error message:", error.message);
        toast.error(`Failed to create post: ${error.message || "Unknown error"}`);
      }
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm("Are you sure you want to delete this post?")) {
        return;
    }

    try {
        await ForumService.deletePost(topic.id, postId);
        setDiscussions(prev => prev.filter(p => p.id !== postId));
        toast.success("Post deleted");
    } catch (error) {
        console.error("Failed to delete post:", error);
        toast.error("Failed to delete post");
    }
  };

  // Filter discussions based on search query, and drop posts from blocked users.
  const filteredDiscussions = discussions.filter((post) =>
    !isBlockedContent(post) && (
      post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleJoinToggle = async () => {
    if (isJoined) {
        // LEAVING — rejoining is immediate; there is no cooldown.
        try {
            await leaveForum(topic.id);
            setIsJoined(false);
            toast.success(`Left ${topic.title || topic.name || 'Forum'}!`);
        } catch (error) {
            toast.error("Failed to leave forum");
        }
    } else {
        // JOINING
        try {
            await joinForum(topic.id);
            setIsJoined(true);
        } catch (error) {
            // Error is handled and toasted by UserDataContext
            console.error("Join forum failed:", error);
        }
    }
  };

  return (
    <div
      className="flex flex-col h-screen bg-light-pattern dark:bg-[#0D0D0D] overflow-hidden"
      style={{ height: '100dvh' }}
    >

      {/* Main Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto relative w-full scrollbar-hide">
         <div className="fixed inset-0 pointer-events-none z-0">
           <ParallaxBackground speed={0.05} />
         </div>

         {/* Content Container */}
         <div className="relative z-10 w-full max-w-2xl mx-auto min-h-screen pb-20">
           
           {/* 1. Header & Banner Section (Scrolls Away) */}
           <div className="relative shadow-sm border-b border-black/10 dark:border-white/10 overflow-hidden">
             {/* Background Image logic matched to Explore screen */}
             <div className="absolute inset-0 z-0">
               {(topic.bannerUrl || topic.image || topic.bannerImage) ? (
                 <img
                   src={topic.bannerUrl || topic.image || topic.bannerImage}
                   alt={topic.title || topic.name}
                   className="w-full h-full object-cover opacity-100"
                 />
               ) : (
                 <div
                   className="w-full h-full"
                   style={{ background: getGroupGradient(topic.id) }}
                 />
               )}
               <div 
                 className="absolute inset-0 backdrop-blur-[2px]" 
                 style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 20%, rgba(0,0,0,0.85) 100%)' }}
               />
             </div>

             {/* Header Content */}
             <div 
                className="relative z-10 px-4 pb-4 w-full"
                style={{ paddingTop: 'max(40px, calc(env(safe-area-inset-top) + 20px))' }}
             >
                <div className="flex-1 min-w-0 mb-3">
                    <h1 
                      className="text-2xl font-bold text-white flex items-center gap-3 drop-shadow-lg leading-tight"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' }}
                    >
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={onBack}
                        className="p-2 mr-1 rounded-full bg-black/30 backdrop-blur-lg flex items-center justify-center hover:bg-black/50 transition-colors border border-white/10 shrink-0"
                        aria-label="Go back"
                      >
                        <ArrowLeft className="w-5 h-5 text-white" />
                      </motion.button>
                      <span 
                        className={`${isTitleExpanded ? '' : 'truncate'} cursor-pointer hover:opacity-80 transition-opacity whitespace-pre-wrap`}
                        onClick={() => setIsTitleExpanded(!isTitleExpanded)}
                      >
                        {topic.title || topic.name}
                      </span>
                      {(topic.ownerId && (userProfile?.id || firebaseUser?.uid)) && 
                       String(topic.ownerId) === String(userProfile?.id || firebaseUser?.uid) && (
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setShowEditModal(true)}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/20 backdrop-blur-md shrink-0"
                        >
                          <Settings className="w-4 h-4 text-white" />
                        </motion.button>
                      )}
                    </h1>
                    
                    <div 
                      className="flex items-center gap-3 text-white/90 text-xs font-medium mt-1 drop-shadow-md"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000' }}
                    >
                      <button 
                        onClick={() => setShowMembersModal(true)}
                        className="bg-black/30 rounded-full px-2 py-0.5 backdrop-blur-md flex items-center gap-1 border border-white/10 hover:bg-black/50 transition-colors"
                      >
                        <Users className="w-3 h-3" />
                         {formatMemberCount(topic.memberCount || 1)}
                      </button>
                      {ownerInfo && (
                          <span className="text-xs opacity-90 flex items-center gap-1">
                              by <span className="font-bold text-white max-w-[100px] truncate">{ownerInfo.name}</span>
                          </span>
                      )}
                    </div>

                    {/* Tags — banner pill style matches Tribe/Forum group screens */}
                    {(topic.tags || topic.identities) && (topic.tags || topic.identities).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(topic.tags || topic.identities).slice(0, 5).map((tag: string, index: number) => (
                          <span
                            key={index}
                            className="text-[11px] font-bold text-white rounded-full whitespace-nowrap"
                            style={{ padding: '4px 12px', background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                </div>

                {/* Description with 'Show More' Toggle and Outline */}
                {topic.description && (
                  <div className="mt-3 mb-1">
                    <p 
                      className="text-xs text-white leading-relaxed"
                      style={{ 
                        textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' // Heavy black outline
                      }}
                    >
                      {topic.description}




                    </p>











                  </div>
                )}

                {/* Main Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => isOwner ? setShowEditModal(true) : handleJoinToggle()}
                    className={`flex-1 h-10 ${isJoined || isOwner
                      ? (isOwner ? 'bg-white/20 backdrop-blur-md border border-white/20 hover:bg-white/30' : 'bg-green-500 hover:bg-green-600 border-none')
                      : 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 border-none'
                      } text-white rounded-xl font-bold shadow-lg`}
                  >
                    {isOwner ? (
                      <div className="flex items-center gap-2"><Settings className="w-4 h-4" /> Settings</div>
                    ) : (
                      isJoined ? '✓ Joined' : 'Join Forum'
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      if (!isJoined) {
                          toast.error("Members only", { description: "Join this forum to enable notifications" });
                          return;
                      }
                      setNotificationsEnabled(!notificationsEnabled)
                    }}
                    className={`w-10 h-10 ${notificationsEnabled
                      ? 'bg-green-500 hover:bg-green-600 text-white border-none'
                      : 'bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/20'
                      } rounded-xl p-0 shadow-lg`}
                  >
                    {notificationsEnabled ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                  </Button>
                  {/* Forums had no sharing at all before this: no invite code, no
                      landing page, no entry point. */}
                  <Button
                    onClick={(e) => { e.stopPropagation(); setShowShareGroup(true); }}
                    className="w-10 h-10 bg-white/20 backdrop-blur-md text-white hover:bg-white/30 border border-white/20 rounded-xl p-0 shadow-lg shrink-0 flex items-center justify-center"
                    aria-label="Share this forum"
                  >
                    <Share2 className="w-5 h-5" />
                  </Button>
                </div>
             </div>
           </div>

           {/* 2. Sticky Search Bar Section */}
           <div className="sticky top-0 z-40 px-4 py-3 bg-[#fdfcf0]/95 dark:bg-[#0D0D0D]/95 backdrop-blur-md border-b border-black/5 dark:border-white/5 shadow-sm transition-colors duration-300">
             <div className="flex gap-2">
               <div className="relative flex-1">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666] dark:text-[#999999]" />
                 <Input
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   placeholder="Search yaps..."
                   className="w-full pl-9 h-10 bg-black/5 dark:bg-white/10 border-none text-black dark:text-white placeholder:text-black/50 dark:placeholder:text-white/50 rounded-xl focus-visible:ring-1 focus-visible:ring-[#F39C12] dark:focus-visible:ring-[#4478ea]"
                 />
               </div>
               <Button
                  onClick={() => setShowNewPostModal(true)}
                  className="h-10 px-4 text-white rounded-xl font-bold shadow-sm transition-all bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90"
                >
                  <Plus className="w-5 h-5" />
                </Button>
             </div>
           </div>
           
           <div className="p-4 space-y-4">
             {/* Owner Tools */}
             {topic.ownerId === userProfile?.id && (
               <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30">
                 <div className="flex items-center justify-between">
                   <div>
                     <h4 className="text-red-700 dark:text-red-400 font-medium">Owner Tools</h4>
                     <p className="text-xs text-red-600/80 dark:text-red-400/70">Manage forum ownership</p>
                   </div>
                   <Button
                     variant="outline"
                     onClick={() => {
                       window.dispatchEvent(new CustomEvent('openPassOwnershipModal', {
                         detail: {
                           entityType: 'forum',
                           entityId: topic.id,
                           lastTransferAt: topic.lastOwnershipTransferAt
                         }
                       }));
                     }}
                     className="bg-white dark:bg-transparent border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 h-9"
                   >
                     Pass Ownership
                   </Button>
                 </div>
               </div>
            )}

            {/* Discussion Posts */}
            {loading ? (
              <p className="text-center text-[#666666] dark:text-[#E5E5E5]">Loading discussion...</p>
            ) : (filteredDiscussions.length === 0 || !filteredDiscussions) ? (
              <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                <div className="w-20 h-20 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mb-6">
                  <MessageCircle className="w-10 h-10 text-[#999999]/40" />
                </div>
                <p className="text-[#666666] dark:text-[#E5E5E5] text-lg font-medium">
                  No Yaps yet
                </p>
                <p className="text-[#999999] text-sm mt-1">Be the first to start the discussion!</p>
              </div>
            ) : (
              filteredDiscussions.map((post) => (
                <PressableCard
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleYapClick(post)}
                  className="p-4 rounded-2xl bg-[#F5F5F5] dark:bg-white/5 border border-black/5 dark:border-white/10 hover:bg-[#FAF8F5] dark:hover:bg-white/10 transition-colors cursor-pointer card-shadow"
                >
                  {/* Author Info */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Avatar 
                  className={`w-10 h-10 cursor-pointer ${post.rank ? getRankRingParams(post.rank).ringClass : 'border-2 border-[#F39C12] dark:border-[#4478ea]'}`}
                  onClick={() => handleViewProfile(post.author)}
                >
                        <AvatarImage src={post.avatar} />
                        <AvatarFallback>{post.author[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <h4 className="text-black dark:text-white flex items-center gap-1.5 leading-tight">
                          <span className="truncate">{post.author}</span>
                          {(post.authorCountryFlag || post.country) && (
                            <FlagImage
                              countryName={post.authorCountryFlag || post.country}
                              size="w80"
                              className="w-3.5 h-2.5 rounded shrink-0 mb-0.5"
                            />
                          )}
                          {(post.rank || (post as any).authorRank) && (
                            <RankBadge rank={post.rank || (post as any).authorRank} size={18} />
                          )}
                        </h4>
                        <p className="text-xs text-[#666666] dark:text-[#E5E5E5] mt-0.5">{post.time}</p>
                        {post.userId && String(post.userId) !== String(currentUserId) && isFriend(String(post.userId)) && (
                          <div className="mt-0.5">
                            <FriendBadge />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 3-dot menu */}
                    {/* Shared menu — same reasoning as the tribe surface.
                        `group` carries forum privacy, which Download's gate
                        reads and which fails closed without it. */}
                    <PostMenu
                      post={post as any}
                      surface="group"
                      collectionName="forum_posts"
                      group={{
                        id: String(topic?.id || ''),
                        isPublic: (topic as any)?.isPublic,
                        isMember: isForumMember(String(topic?.id || ''), (topic as any)?.ownerId),
                      }}
                      onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                    />
                  </div>

                  {/* Content */}
                  <FormattedText
                    text={post.content}
                    className="text-[#333333] dark:text-[#E5E5E5] mb-3 leading-relaxed block"
                  />

                  {/* Poll. Forum posts keep their votes and tallies under
                      forum_posts, so the card is pointed at that collection. */}
                  {(post as any).poll?.options?.length > 0 && (
                    <div className="mb-3">
                      <PollCard
                        yapId={String(post.id)}
                        poll={(post as any).poll}
                        collectionName="forum_posts"
                      />
                    </div>
                  )}

                  {/* Topic label (cross-posted yaps) — below the text, above media.
                      Suppressed when it points at the forum currently being viewed,
                      so cross-posts and forum-exclusive posts look identical here. */}
                  {post.forumTopic?.forumId && post.forumTopic.forumId !== topic.id && (
                    <div className="mb-3">
                      <YapTopicLabel forumId={post.forumTopic.forumId} forumName={post.forumTopic.forumName} />
                    </div>
                  )}

                  {/* Images — shared compact grid (clipped, Twitter-style) */}
                  {(post.images?.length || post.image) ? (
                    <div className="mb-3">
                      <YapImageGrid images={post.images?.length ? post.images : [post.image!]} />
                    </div>
                  ) : null}

                  {/* Attached video (optional trim window) */}
                  {(post as any).videoUrl && (
                    <div className="mb-3">
                      <YapVideoPlayer
                        videoUrl={(post as any).videoUrl}
                        trimStart={(post as any).videoTrimStart}
                        trimEnd={(post as any).videoTrimEnd}
                        videoWidth={(post as any).videoWidth}
                        videoHeight={(post as any).videoHeight}
                        authorName={post.author}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-6">
                    <LikeButton
                      postId={post.id}
                      postType="forum"
                      originalLikeCount={post.likes}
                    />
                    <button className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors touch-clickable">
                      <MessageCircle className="w-4 h-4" />
                      <span className="text-sm">{post.comments}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Share is allowed for non-members (read-only)
                        setSelectedYapToShare(post);
                        setShowShareModal(true);
                      }}
                      className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors touch-clickable"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                  </div>
                </PressableCard>
              ))
            )}
           </div>
         </div>
      </div>

      {/* New Post Modal */}
      <AnimatePresence>
        {showNewPostModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewPostModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-light-pattern rounded-3xl flex flex-col max-h-[90vh] shadow-2xl"
            >
              {/* Header */}
              <div className="p-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/30 backdrop-blur-lg"
                style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
                <div className="flex items-center justify-center mb-4 relative">
                  <h2 className="text-xl text-black dark:text-white">Create Forum Post</h2>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowNewPostModal(false)}
                    className="absolute right-0 text-[#666666] dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </motion.button>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 min-h-0 overflow-y-auto p-6">
                {/* Info Banner */}
                <div className="mb-4 p-3 rounded-xl bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/30 dark:border-[#4478ea]/30 flex items-center">
                  <Info className="w-4 h-4 mr-2 text-[#F39C12] dark:text-[#4478ea] shrink-0" />
                  <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">
                    This yap will only be visible in this forum
                  </p>
                </div>

                {/* Text Area */}
                <Textarea
                  value={newPostContent}
                  onChange={(e) => {
                    // Limit to 300 characters
                    if (e.target.value.length <= 300) {
                      setNewPostContent(e.target.value);
                    }
                  }}
                  placeholder="Share your thoughts..."
                  className="w-full min-h-[150px] bg-[#F5F5F5] dark:bg-[#2a2a2a] border-black/10 dark:border-white/20 text-black dark:text-white resize-none rounded-xl mb-4"
                />

                {/* @Mention Indicator while typing */}
                {newPostContent.match(/@[\w\.]+/g) && (
                  <motion.div 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 flex flex-wrap gap-1.5"
                  >
                    <span className="text-[10px] text-[#666666] dark:text-[#999999] uppercase font-bold flex items-center mr-1"> Mentioning: </span>
                    {Array.from(new Set(newPostContent.match(/@[\w\.]+/g))).map((mention, i) => (
                      <span 
                        key={i}
                        className="px-2 py-0.5 rounded-full bg-[#F39C12]/10 dark:bg-[#4478ea]/10 border border-[#F39C12]/20 dark:border-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea] text-[10px] font-bold"
                      >
                        {mention}
                      </span>
                    ))}
                  </motion.div>
                )}

                {/* Character Count */}
                <p className={`text-sm mb-4 ${newPostContent.length >= 300 ? 'text-[#E74C3C] dark:text-[#2d1ef1]' : 'text-[#999999]'}`}>
                  {newPostContent.length} / 300 characters
                </p>

                {/* Image Counter */}
                {newPostImages.length > 0 && (
                  <p className="text-sm text-[#666666] dark:text-[#E5E5E5] mb-4">
                    📷 {newPostImages.length} / 2 images added
                  </p>
                )}

                <Button
                  onClick={handleTriggerUpload}
                  disabled={newPostImages.length >= 2}
                  className={`w-full h-12 rounded-xl mb-4 ${newPostImages.length >= 2
                    ? 'bg-[#E5E5E5] dark:bg-[#2a2a2a] text-[#999999] cursor-not-allowed'
                    : 'bg-[#2C3E50] hover:bg-[#34495e] text-white'
                    }`}
                >
                  <ImageIcon className="w-5 h-5 mr-2" />
                  {newPostImages.length >= 2 ? 'Maximum Images Added' : 'Add Image'}
                </Button>
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleAddImage}
                  className="hidden"
                  accept="image/*"
                />

                {/* Display Added Images */}
                {newPostImages.map((image, index) => (
                  <div key={index} className="relative mb-4">
                    <img
                      src={image}
                      alt={`Added Image ${index + 1}`}
                      className="w-full h-auto rounded-xl"
                    />
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>
                ))}

              </div>{/* end scrollable body */}

              {/* Actions — always visible at bottom */}
              <div className="shrink-0 flex gap-3 px-6 py-4 border-t border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#1a1a1a] rounded-b-3xl">
                <Button
                    onClick={() => setShowNewPostModal(false)}
                    variant="outline"
                    className="flex-1 h-12 bg-transparent border-2 border-[#2C3E50] text-[#2C3E50] dark:text-white hover:bg-[#2C3E50]/10 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePost}
                    disabled={!newPostContent.trim()}
                    className={`flex-1 h-12 rounded-xl ${!newPostContent.trim()
                      ? 'bg-[#E5E5E5] dark:bg-[#2a2a2a] text-[#999999] cursor-not-allowed'
                      : 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white'
                      }`}
                  >
                    Yap to Forum
                  </Button>
                </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share the forum itself. Outside the selectedYapToShare guard below —
          that one gates sharing a POST, which is a different thing. */}
      <ShareGroupModal
        isOpen={showShareGroup}
        onClose={() => setShowShareGroup(false)}
        group={{
          id: String(topic.id),
          name: topic.title || topic.name || 'Forum',
          type: 'forum',
          bannerUrl: topic.bannerUrl || topic.image || topic.bannerImage || '',
          isPublic: topic.isPublic !== false,
          inviteCode: (topic as any).inviteCode,
        }}
      />

      {/* Share Yap Modal */}
      {selectedYapToShare && (
        <ShareYapModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedYapToShare(null);
          }}
          yap={selectedYapToShare}
        />
      )}
      {/* Edit Group Settings Modal */}
      {topic.ownerId === userProfile?.id && (
        <GroupSettingsModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          group={topic}
          type="forum"
          onUpdate={(updated: any) => setTopic((prev: any) => ({ ...prev, ...updated }))}
        />
      )}
      
      {/* Group Members Modal */}
      <GroupMembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        groupId={topic.id}
        type="forum"
      />
    </div>
  );
}
